<template>
  <div class="info-content">
    <slot></slot>
  </div>
</template>

<script setup>
/**
 * Enveloppe du contenu d'une bulle d'aide. Elle ne rend rien : elle fournit la
 * grammaire visuelle commune aux ~50 composants `*Info.vue`, qui écrivent tous
 * le même vocabulaire de classes.
 *
 *   .info-lead                 le bandeau « L'essentiel », en tête de l'aide
 *   .info-section > h3 > i     un bloc, son titre et sa pastille d'icône
 *   .info-item                 un sous-point, séparé du suivant par un filet
 *     .info-item-header        son intitulé
 *     .info-item-content       son explication
 *   .info-note                 une remarque en encadré teinté ; la teinte suit
 *                              l'icône (ampoule = astuce, triangle = attention)
 *                              ou les classes `is-tip` / `is-warning`
 *   .info-table                un tableau de valeurs (cibles, seuils, poids)
 *   ul.info-legend             une légende de signes : une ligne = un signe
 *                              + sa phrase
 *     .info-legend-mark        la gouttière de 28 px qui porte le signe ;
 *                              teinte par `--info-legend-fill` posé en ligne
 *                              (un jeton CSS, jamais une couleur en dur)
 *       is-outlined | is-dashed | is-stripes   aplats de 28 × 14 (filet
 *                              clair, tireté `--info-legend-stroke`,
 *                              hachures `--info-legend-angle`)
 *       is-split > .info-legend-part           aplat + barre intérieure
 *                              (`--info-legend-part-fill`, `-part-width`)
 *       is-glyph [is-diamond | is-round | is-ringed]
 *                              signe centré à sa taille réelle
 *                              (`--info-legend-w` / `-h` / `-radius` / `-offset`)
 *         is-fit               gouttière à la largeur du signe, calée à
 *                              gauche (+ `--info-legend-inset`)
 *       is-pill                échantillon de texte (« 3 appels · 2 devis »)
 *       is-icon                icône Font Awesome atténuée
 *
 * Ajouter une classe ici plutôt que du CSS dans un `*Info.vue` : c'est ce qui
 * garde les 50 aides identiques d'une page à l'autre.
 */
</script>

<style scoped>
.info-content {
  color: var(--color-text-secondary);
}

/* ═══ Le bandeau d'ouverture ═══
   Ce qu'il faut avoir compris si on ne lit que trois lignes. */
:deep(.info-lead) {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  padding: var(--spacing-4) var(--spacing-5);
  margin-bottom: var(--spacing-6);
}

:deep(.info-lead::before) {
  content: 'L\'essentiel';
  display: block;
  margin-bottom: var(--spacing-2);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-primary-dark);
}

:deep(.info-lead p) {
  font-size: var(--font-size-base);
  line-height: var(--line-height-relaxed);
}

:deep(.info-lead p:last-child) {
  margin-bottom: 0;
}

/* ═══ Les blocs ═══ */
:deep(.info-section) {
  margin-bottom: var(--spacing-6);
}

:deep(.info-section:last-child) {
  margin-bottom: 0;
}

:deep(h3) {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  margin: 0 0 var(--spacing-3);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary-dark);
  line-height: 1.4;
}

/* Pastille d'icône : le même repère que les sections des fiches détail. */
:deep(h3 i) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-lg);
  background: var(--color-primary-lightest);
  color: var(--color-primary);
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

:deep(p) {
  margin: 0 0 var(--spacing-2);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
  color: var(--color-text-secondary);
}

:deep(strong) {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

:deep(ul),
:deep(ol) {
  margin: var(--spacing-2) 0 var(--spacing-3);
  padding-left: var(--spacing-5);
  font-size: var(--font-size-sm);
  line-height: 1.8;
  color: var(--color-text-secondary);
}

:deep(li) {
  margin-bottom: var(--spacing-1);
}

/* ═══ Les sous-points ═══
   Des filets, pas des cartes : une aide n'est pas un tableau de bord. */
:deep(.info-item) {
  padding: var(--spacing-3) 0;
  border-bottom: 1px solid var(--color-border-light);
}

:deep(.info-item:last-child) {
  padding-bottom: 0;
  border-bottom: none;
}

:deep(.info-item-header) {
  margin-bottom: var(--spacing-1);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

:deep(.info-item-content) {
  font-size: var(--font-size-xs);
  line-height: 1.7;
  color: var(--color-text-tertiary);
}

:deep(.info-item-content p),
:deep(.info-item-content ul),
:deep(.info-item-content ol) {
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

:deep(.info-item-content p:last-child),
:deep(.info-item-content ul:last-child),
:deep(.info-item-content ol:last-child) {
  margin-bottom: 0;
}

/* ═══ Les remarques ═══
   L'icône est positionnée, jamais posée en élément flex : une note contient du
   texte enrichi (<strong>, <a>), et un conteneur flex en ferait autant de
   colonnes — la phrase se briserait en morceaux. */
:deep(.info-note) {
  position: relative;
  margin: var(--spacing-3) 0 0;
  padding: var(--spacing-2) var(--spacing-4) var(--spacing-2) 38px;
  border-radius: var(--radius-sm);
  background: var(--color-info-lighter);
  font-size: var(--font-size-xs);
  line-height: 1.65;
  color: var(--color-text-secondary);
}

:deep(.info-note i) {
  position: absolute;
  left: var(--spacing-4);
  top: 11px;
  font-size: 0.8rem;
  color: var(--color-info-dark);
}

:deep(.info-note a) {
  color: var(--color-primary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
}

:deep(.info-note a:hover) {
  text-decoration: underline;
}

/* La teinte se déduit de l'icône déjà écrite dans les aides : une ampoule est
   une astuce, un triangle une mise en garde. Aucune n'a eu à être retouchée. */
:deep(.info-note:has(i.fa-lightbulb)),
:deep(.info-note.is-tip) {
  background: var(--color-warning-lightest);
}

:deep(.info-note:has(i.fa-lightbulb) i),
:deep(.info-note.is-tip i) {
  color: var(--color-warning-dark);
}

:deep(.info-note:has(i.fa-exclamation-triangle)),
:deep(.info-note:has(i.fa-triangle-exclamation)),
:deep(.info-note.is-warning) {
  background: var(--color-danger-lightest);
}

:deep(.info-note:has(i.fa-exclamation-triangle) i),
:deep(.info-note:has(i.fa-triangle-exclamation) i),
:deep(.info-note.is-warning i) {
  color: var(--color-danger-dark);
}

/* ═══ Les légendes de signes ═══
   La légende montre le signe tel qu'on le cherchera dans la frise, à sa taille
   réelle, pas une version agrandie. La couleur n'est jamais dans la classe :
   elle arrive par un jeton passé en ligne (`--info-legend-fill`), ce qui évite
   tout `<style>` dans les `*Info.vue`. Retrait et marges de la liste restent
   ceux de `ul` ci-dessus. */
:deep(ul.info-legend) {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

:deep(.info-legend > li) {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  font-size: 0.9rem;
  line-height: 1.5;
}

:deep(.info-legend-mark) {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 28px;
  height: 14px;
  margin-top: 3px;
  border-radius: var(--radius-sm);
  background: var(--info-legend-fill, transparent);
}

:deep(.info-legend-mark.is-outlined) {
  border: 1px solid var(--color-border-light);
}

:deep(.info-legend-mark.is-dashed) {
  border: 1px dashed var(--info-legend-stroke);
}

:deep(.info-legend-mark.is-stripes) {
  background: repeating-linear-gradient(
    var(--info-legend-angle, 45deg),
    var(--color-bg-tertiary) 0 5px,
    var(--color-bg-secondary) 5px 10px
  );
}

:deep(.info-legend-mark.is-split) {
  display: inline-flex;
  align-items: center;
}

:deep(.info-legend-part) {
  width: var(--info-legend-part-width, 12px);
  height: 14px;
  border-radius: var(--radius-sm);
  background: var(--info-legend-part-fill);
}

/* Jalon : le signe est dessiné par ::before, centré dans la gouttière. */
:deep(.info-legend-mark.is-glyph) {
  height: auto;
  margin-top: var(--info-legend-offset, 5px);
  display: inline-flex;
  justify-content: center;
  background: none;
  border-radius: 0;
}

/* Signe qui n'est pas centré : la gouttière prend sa largeur, calée à gauche. */
:deep(.info-legend-mark.is-glyph.is-fit) {
  width: auto;
  justify-content: flex-start;
  margin-left: var(--info-legend-inset, 0);
}

:deep(.info-legend-mark.is-glyph::before) {
  content: '';
  display: block;
  width: var(--info-legend-w, 8px);
  height: var(--info-legend-h, var(--info-legend-w, 8px));
  border-radius: var(--info-legend-radius, 0);
  background: var(--info-legend-fill);
}

:deep(.info-legend-mark.is-diamond::before) {
  transform: rotate(45deg);
}

:deep(.info-legend-mark.is-round::before) {
  border-radius: var(--radius-full);
}

:deep(.info-legend-mark.is-ringed::before) {
  box-shadow: 0 0 0 2px var(--color-bg-primary), 0 0 0 3px var(--info-legend-fill);
}

:deep(.info-legend-mark.is-pill) {
  width: auto;
  height: auto;
  margin-top: 2px;
  padding: 0 var(--spacing-1);
  border: 1px solid var(--color-border);
  background: var(--color-bg-secondary);
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

:deep(.info-legend-mark.is-icon) {
  height: auto;
  display: inline-flex;
  justify-content: center;
  background: none;
  color: var(--color-text-muted);
}

/* ═══ Les tableaux de valeurs ═══ */
:deep(.info-table) {
  width: 100%;
  border-collapse: collapse;
  margin: var(--spacing-1) 0 var(--spacing-3);
}

:deep(.info-table th) {
  padding: 0 var(--spacing-2) var(--spacing-2) 0;
  border-bottom: 1px solid var(--color-border);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  text-align: left;
}

:deep(.info-table td) {
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) 0;
  border-bottom: 1px solid var(--color-border-light);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

:deep(.info-table tr:last-child td) {
  border-bottom: none;
}

:deep(.info-table th.num),
:deep(.info-table td.num) {
  padding-right: 0;
  text-align: right;
}

:deep(.info-table td.num) {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
}
</style>
